import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { HardHat, Loader2, Ruler, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const M2_TO_FT2 = 10.7639;
const M_TO_FT = 3.28084;

export default function PlanPublicPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [accepted, setAccepted] = useState(null); // {name} once accepted
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [acceptErr, setAcceptErr] = useState('');

  useEffect(() => {
    api.get(`/public/plans/${token}`)
      .then((r) => { setData(r.data); if (r.data?.accepted) setAccepted({ name: r.data.accepted_name }); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const acceptDesign = async () => {
    setAccepting(true); setAcceptErr('');
    try {
      const { data: r } = await api.post(`/public/plans/${token}/accept`, { name, note });
      setAccepted({ name: r.accepted_name });
    } catch (e) {
      if (e?.response?.status === 409) setAccepted({ name: null });
      else setAcceptErr(e?.response?.data?.detail || 'Could not accept — please try again');
    } finally { setAccepting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400 font-mono"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center" data-testid="plan-public-error">
        <Ruler size={40} className="text-slate-300 mb-3" />
        <h1 className="font-display font-semibold text-2xl uppercase tracking-tight">Plan unavailable</h1>
        <p className="text-slate-500 mt-2">This link has expired or was removed.</p>
        <Link to="/" className="tm-btn inline-flex mt-5">Go to TradesMate</Link>
      </div>
    );
  }

  const m = data.meta || {};
  const unit = m.unit || 'm';
  const fmtA = (a) => unit === 'm' ? `${(a || 0).toFixed(1)} m²` : `${((a || 0) * M2_TO_FT2).toFixed(1)} ft²`;
  const fmtL = (v) => unit === 'm' ? `${(v || 0).toFixed(1)}m` : `${((v || 0) * M_TO_FT).toFixed(1)}ft`;
  const totals = m.totals || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="plan-public-page">
      <Seo title={`${data.business_name} — Design plan`} noindex />
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6 flex items-center gap-3">
          {data.logo_data
            ? <img src={data.logo_data} alt="" className="h-12 w-auto bg-white rounded p-1" />
            : <span className="inline-flex items-center gap-2 font-display font-semibold text-lg text-slate-900"><span className="w-7 h-7 rounded-full bg-brand text-brand-ink grid place-items-center"><HardHat size={14} /></span> TradesMate</span>}
          <div>
            <h1 className="font-display font-semibold text-2xl sm:text-3xl uppercase tracking-tight">{data.business_name}</h1>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Garden / driveway design plan</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {(m.plotW && m.plotL) ? (
          <p className="text-sm text-slate-600" data-testid="plan-public-plot">Plot: <strong>{fmtL(m.plotW)} × {fmtL(m.plotL)}</strong> — total {fmtA(m.plotW * m.plotL)}</p>
        ) : null}

        <figure className="tm-card overflow-hidden">
          <figcaption className="text-[11px] uppercase tracking-widest text-slate-500 px-3 pt-3 font-semibold">Plan</figcaption>
          <img src={`${API}${data.plan_url}`} alt="Design plan" className="w-full bg-white p-2" data-testid="plan-public-plan-img" />
        </figure>

        {data.iso_url && (
          <figure className="tm-card overflow-hidden">
            <figcaption className="text-[11px] uppercase tracking-widest text-slate-500 px-3 pt-3 font-semibold">3D preview</figcaption>
            <img src={`${API}${data.iso_url}`} alt="3D preview" className="w-full bg-[#dfeaf2] p-2" data-testid="plan-public-iso-img" />
          </figure>
        )}

        {data.render_url && (
          <figure className="tm-card overflow-hidden">
            <figcaption className="text-[11px] uppercase tracking-widest text-brand px-3 pt-3 font-semibold">AI concept render</figcaption>
            <img src={`${API}${data.render_url}`} alt="AI render" className="w-full bg-white p-2" data-testid="plan-public-render-img" />
          </figure>
        )}

        {totals.length > 0 && (
          <div className="tm-card p-4" data-testid="plan-public-materials">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-3 font-semibold">Materials &amp; estimated quantities</p>
            <div className="space-y-3">
              {totals.map((t) => (
                <div key={t.name} className="border-b border-slate-100 pb-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-bold">{t.name}</span>
                    <span className="font-mono font-bold">{fmtA(t.a)}</span>
                  </div>
                  {t.lines?.length > 0 && (
                    <ul className="mt-1 pl-2 border-l border-slate-200 space-y-0.5">
                      {t.lines.map((l) => <li key={l.name} className="text-xs text-slate-500 flex justify-between gap-3"><span>{l.name}</span><span className="font-mono text-slate-700 whitespace-nowrap">{l.text}</span></li>)}
                    </ul>
                  )}
                </div>
              ))}
              <div className="flex justify-between pt-1"><span className="font-semibold">Total covered</span><span className="font-semibold font-mono">{fmtA(m.totalArea)}</span></div>
              <p className="text-[10px] text-slate-400">Quantities are indicative estimates — confirm on site.</p>
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-400">Indicative plan for discussion — measurements to be confirmed on site.</p>

        {accepted ? (
          <div className="tm-card p-5 border border-emerald-600 bg-emerald-50 text-center" data-testid="plan-accepted-banner">
            <CheckCircle2 className="mx-auto text-emerald-600 mb-2" size={34} />
            <h2 className="font-display font-semibold text-xl uppercase tracking-tight text-emerald-800">Design accepted</h2>
            <p className="text-emerald-700 text-sm mt-1">{accepted.name ? `Thanks ${accepted.name} — ` : ''}{data.business_name} has been notified and will be in touch to book the job in.</p>
          </div>
        ) : (
          <div className="tm-card p-5 border border-slate-200" data-testid="plan-accept-box">
            <h2 className="font-display font-bold text-lg uppercase tracking-tight">Happy with this design?</h2>
            <p className="text-slate-600 text-sm mt-1 mb-3">Accept it in one tap and {data.business_name} will get straight back to you to book it in.</p>
            <div className="grid sm:grid-cols-2 gap-2 mb-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" data-testid="plan-accept-name" className="border border-slate-300 px-3 py-2 text-sm focus:border-emerald-600 outline-none" />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)" data-testid="plan-accept-note" className="border border-slate-300 px-3 py-2 text-sm focus:border-emerald-600 outline-none" />
            </div>
            {acceptErr && <p className="text-rose-600 text-sm mb-2" data-testid="plan-accept-error">{acceptErr}</p>}
            <button onClick={acceptDesign} disabled={accepting} data-testid="plan-accept-btn" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-emerald-600 text-white uppercase tracking-wide px-5 py-3 border border-emerald-700 hover:bg-emerald-700 transition-colors disabled:opacity-60 font-semibold">
              {accepting ? <><Loader2 size={16} className="animate-spin" /> Accepting…</> : <><CheckCircle2 size={16} /> Accept this design</>}
            </button>
          </div>
        )}

        <div className="text-center pt-2">
          <p className="text-slate-500 text-sm">Plan created with <Link to="/" className="text-brand font-bold hover:underline">TradesMate</Link> — free tools for UK tradesmen.</p>
        </div>
      </main>
    </div>
  );
}
